<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Increment List
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                        >CSV</v-btn>
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                        >EXCEL</v-btn>
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                        >PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right pr-2">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="goTo('/hrm/add-increment')"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                      >
                        Add Increment
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-card class="overflow-x-auto">
                  <v-data-table
                    :headers="headers"
                    :items="increments"
                    :loading="loading"
                    v-model:items-per-page="itemsPerPage"
                    :search="search"
                    :hover="true"
                    density="compact"
                    class="min-w-[800px]"
                  >
                    <template v-slot:item.index="{ index }">
                      {{ ++index }}
                    </template>

                    <template v-slot:item.employee="{ item }">
                      {{ item.employee?.name }}
                    </template>

                    <template v-slot:item.increment_type="{ item }">
                      {{ getIncrementTypeText(item.increment_type) }}
                    </template>

                    <template v-slot:item.increment_amount="{ item }">
                      ৳{{ formatNumber(item.increment_amount) }}
                    </template>

                    <template v-slot:item.increment_date="{ item }">
                      <span class="text-caption">{{ formatDate(item.increment_date) }}</span>
                    </template>
                    
                    <template v-slot:item.effect_date="{ item }">
                      <span class="text-caption">{{ formatDate(item.effect_date) }}</span>
                    </template>

                    <template v-slot:item.status="{ item }">
                      <v-chip
                        :color="getStatusColor(item.status)"
                        size="small"
                        variant="tonal"
                      >
                        {{ getStatusText(item.status) }}
                      </v-chip>
                    </template>

                    <template v-slot:item.actions="{ item }">
                      <v-btn
                        @click="editIncrement(item)"
                        icon="mdi-pencil"
                        size="small"
                        color="primary"
                        variant="text"
                      ></v-btn>
                    </template>
                  </v-data-table>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { toast } from 'vue3-toastify'

// Router and Store
const router = useRouter()
const store = useStore()

// Get Vue instance for export functions
const instance = getCurrentInstance()

// Reactive data
const panel = ref([0])
const search = ref('')
const loading = ref(false)
const itemsPerPage = ref(10)
const increments = ref([])

// Table headers
const headers = ref([
  { title: '#SL', key: 'index', sortable: false,  align: 'center' },
  { title: 'Employee Name', key: 'employee_name', sortable: true },
  { title: 'Ref No', key: 'ref_no', sortable: true },
  { title: 'Increment Date', key: 'increment_date', sortable: true },
  { title: 'Effect Date', key: 'effect_date', sortable: true },
  {title : 'Gross Salary', key: 'gross_salary', sortable: true },
  { title: 'Type', key: 'increment_type', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false, width: '100px' }
])

// Options
const incrementTypes = ref([
  { text: 'Normal', value: 'normal' },
  { text: 'Performance', value: 'performance' },
  { text: 'Promotion', value: 'promotion' },
  { text: 'Special', value: 'special' }
])

const statusOptions = ref([
  { text: 'Active', value: 'active' },
  { text: 'Inactive', value: 'inactive' }
])

// Methods
const loadIncrements = async () => {
  loading.value = true
  try {
    const response = await store.dispatch('hrmIncrement/getIncrementList', {
      search: search.value,
      order_by: 'increment_date',
      order_direction: 'desc'
    })
    
    if (response.success) {
      
      increments.value = response.data.map(increment => ({
        id: increment.id,
        ref_no: increment.ref_no,
        employee_name: increment.employee?.name || 'N/A',
        increment_type: increment.increment_type,
        gross_salary: increment.after_gross_salary,
        amount: increment.increment_amount,
        status: increment.status,
        approval_status: increment.approval_status,
        increment_date: increment.increment_date,
        effect_date: increment.effect_date,
        current_basic_salary: increment.current_basic_salary,
        new_basic_salary: increment.new_basic_salary,
        employee: increment.employee,
        // Include after salary data for editing
        after_package: increment.after_package,
        after_gross_salary: increment.after_gross_salary,
        after_basic: increment.after_basic,
        after_house_rent: increment.after_house_rent,
        after_medical: increment.after_medical,
        after_education: increment.after_education,
        after_mobile: increment.after_mobile,
        after_transport: increment.after_transport,
        after_technical: increment.after_technical,
        after_festival: increment.after_festival,
        after_tiffin: increment.after_tiffin,
        after_wash: increment.after_wash,
        after_incentive: increment.after_incentive,
        after_others: increment.after_others,
        after_tax_deduct: increment.after_tax_deduct,
        after_pf: increment.after_pf,
        after_car_use: increment.after_car_use,
        after_gas_bill: increment.after_gas_bill,
        after_electricity_bill: increment.after_electricity_bill,
        after_telephone_bill: increment.after_telephone_bill,
        after_insurance: increment.after_insurance,
        after_others_deduct: increment.after_others_deduct
      }))
    } else {
      toast.error(response.message || 'Failed to load increments')
    }
  } catch (error) {
    console.error('Error loading increments:', error)
    toast.error('Failed to load increments')
  } finally {
    loading.value = false
  }
}

const editIncrement = async (item) => {
  try {
    // Try to fetch complete increment details
    const response = await store.dispatch('hrmIncrement/getIncrementById', item.id)
    
    if (response.success) {
      store.commit('hrmIncrement/setEditingIncrement', response.data)
    } else {
      store.commit('hrmIncrement/setEditingIncrement', item)
    }
  } catch (error) {
    console.error('API call failed, using basic data:', error)
    store.commit('hrmIncrement/setEditingIncrement', item)
  }
  
  router.push('/hrm/add-increment')
}

const goTo = (path) => {
  try {
    // Clear any editing increment state when navigating to add new increment
    if (path === '/hrm/add-increment') {
      store.commit('hrmIncrement/clearEditingIncrement')
    }
    router.push(path)
  } catch (error) {
    console.error('Navigation error:', error)
  }
}

// Export functions
const exportToPDF = () => {
  let serialNumber = 1
  const columns = [
    "#SL", 
    "Employee Name", 
    "Ref No", 
    "Increment Date", 
    "Effect Date", 
    "Gross Salary", 
    "Type", 
    "Status"
  ]
  const rows = increments.value.map((item) => [
    serialNumber++,
    item.employee_name,
    item.ref_no,
    formatDate(item.increment_date),
    formatDate(item.effect_date),
    formatNumber(item.gross_salary),
    getIncrementTypeText(item.increment_type),
    getStatusText(item.status)
  ])
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToPDF(columns, rows, "Increment List", "increment_list_" + instance.proxy.$fileDate())
}

const exportToExcel = () => {
  const data = [[
    "#SL", 
    "Employee Name", 
    "Ref No", 
    "Increment Date", 
    "Effect Date", 
    "Gross Salary", 
    "Type", 
    "Status"
  ]]
  let serialNumber = 1
  
  increments.value.forEach((item) => {
    data.push([
      serialNumber++,
      item.employee_name,
      item.ref_no,
      formatDate(item.increment_date),
      formatDate(item.effect_date),
      formatNumber(item.gross_salary),
      getIncrementTypeText(item.increment_type),
      getStatusText(item.status)
    ])
  })
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToExcel(data, "increment_list_" + instance.proxy.$fileDate())
}

const exportToCSV = () => {
  const data = [[
    "#SL", 
    "Employee Name", 
    "Ref No", 
    "Increment Date", 
    "Effect Date", 
    "Gross Salary", 
    "Type", 
    "Status"
  ]]
  let serialNumber = 1
  
  increments.value.forEach((item) => {
    data.push([
      serialNumber++,
      item.employee_name,
      item.ref_no,
      formatDate(item.increment_date),
      formatDate(item.effect_date),
      formatNumber(item.gross_salary),
      getIncrementTypeText(item.increment_type),
      getStatusText(item.status)
    ])
  })
  
  // Use Vue instance export functions
  instance.proxy.$exportTableToCSV(data, "increment_list_" + instance.proxy.$fileDate())
}


// Utility functions
const formatNumber = (number) => {
  return new Intl.NumberFormat('en-US').format(number || 0)
}

const formatDate = (date) => {
  if (!date) return '-'
  try {
    const dateObj = new Date(date)
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch (error) {
    return '-'
  }
}

// Status helper functions
const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'success'
    case 'inactive':
      return 'error'
    default:
      return 'grey'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'active':
      return 'Active'
    case 'inactive':
      return 'Inactive'
    default:
      return 'Unknown'
  }
}

const getIncrementTypeText = (type) => {
  switch (type) {
    case 'normal':
      return 'Normal'
    case 'performance':
      return 'Performance'
    case 'promotion':
      return 'Promotion'
    case 'special':
      return 'Special'
    default:
      return 'Unknown'
  }
}

const getIncrementTypeColor = (type) => {
  const colors = {
    normal: 'blue',
    performance: 'green',
    promotion: 'purple',
    special: 'orange'
  }
  return colors[type] || 'grey'
}

const getApprovalStatusText = (status) => {
  const statuses = {
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected'
  }
  return statuses[status] || status
}

const getApprovalStatusColor = (status) => {
  const colors = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error'
  }
  return colors[status] || 'default'
}

// Initialize
onMounted(() => {
  // Clear any editing increment state when the list loads
  store.commit('hrmIncrement/clearEditingIncrement')
  loadIncrements()
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

.tab-item {
  border-radius: 8px;
  margin: 2px 8px;
}

.tab-active {
  background-color: rgb(var(--v-theme-primary));
  color: white;
}

.min-w-\[1200px\] {
  min-width: 1200px;
}
</style>
